import useReveal from "@/hooks/useReveal";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

// Project image paths — replace with real /projects/*.jpg uploads when available.
const PROJECTS = [
  {
    name: "Vivanta Bhubaneswar",
    type: "Luxury Hotel · Full Uniform Programme",
    desc: "A complete uniform system for an established luxury property — F&B, front office, housekeeping and concierge.",
    image: "/projects/featured-project-1.jpg",
    fallback:
      "https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    name: "Trident Bhubaneswar",
    type: "Luxury Hotel · Multi-Department",
    desc: "Refined silhouettes and fabric programmes aligned with the property's contemporary interior language.",
    image: "/projects/featured-project-2.jpg",
    fallback:
      "https://images.unsplash.com/photo-1585418694458-dc80a5c20294?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    name: "The World Backwaters",
    type: "Boutique Resort · Uniform Programme",
    desc: "Climate-considered uniforms for a backwater resort experience, with breathable fabrics and tactile detailing.",
    image: "/projects/featured-project-3.jpg",
    fallback:
      "https://images.unsplash.com/photo-1578474846511-04ba529f0b88?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
];

function ProjectCard({ p, i, delay }) {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`featured-project-${i}`}
      className={`reveal reveal-delay-${delay} group`}
    >
      <div className="relative overflow-hidden aspect-[4/5] bg-sandstone">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          onError={(e) => {
            if (e.currentTarget.src !== p.fallback) e.currentTarget.src = p.fallback;
          }}
          className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-espresso/10 group-hover:bg-espresso/5 transition-colors duration-700" />
        {/* Hover caption */}
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 bg-gradient-to-t from-espresso/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="text-[10px] uppercase tracking-[0.28em] text-bone/85">
            {p.type}
          </div>
        </div>
      </div>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <div className="overline-muted">{p.type}</div>
          <h3 className="mt-2 font-serif text-[24px] md:text-[26px] tracking-tight text-espresso">
            {p.name}
          </h3>
          <p className="mt-3 text-[14px] leading-relaxed text-espresso-soft font-light">
            {p.desc}
          </p>
        </div>
        <span className="mt-2 w-10 h-px bg-oxblood shrink-0 group-hover:w-14 transition-all duration-700" />
      </div>
    </article>
  );
}

export default function FeaturedProjects() {
  const headRef = useReveal();
  return (
    <section data-testid="featured-projects" className="py-28 md:py-40">
      <div className="container-luxury">
        <div ref={headRef} className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="overline mb-6">Featured Projects</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[58px] leading-[1.06] text-espresso tracking-tight text-balance">
              A selection of
              <em className="italic text-oxblood"> recent commissions.</em>
            </h2>
          </div>
          <Link
            to="/projects"
            data-testid="view-projects-link"
            className="btn-ghost self-start md:self-end"
          >
            View Projects <ArrowUpRight size={16} />
          </Link>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {PROJECTS.map((p, i) => (
            <ProjectCard key={p.name} p={p} i={i} delay={(i % 4) + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}
